/**
 * Справочник техники — доменная логика без React (ТЗ AI-Economist-2026 ред. 2.1,
 * п. 6.1). Схема паспорта модели, подписи, форматирование чисел, сборка тела
 * запроса из формы и подписи «было → стало».
 *
 * Модуль намеренно без импортов значений (только `import type`): его проверяет
 * `model.test.mjs` на встроенном node:test, а Node не знает путей-алиасов.
 *
 * Паспорт — словарь emm_book.v1 (`doc-processor/app/parsers/emm_book.py`):
 * коды полей, числа строками ровно как в ячейке Excel, поля по зонам и годам —
 * вложенные словари. Деньги и нормы через float не проходят: форматирование и
 * разбор ввода — на строках.
 */
import type {
  FleetDiffEntry,
  FleetModel,
  FleetModelData,
  FleetRevisionAction,
  FleetStatus,
  FleetUnit,
  FleetUnitData,
} from '@shared/api';

export const NBSP = '\u00A0';
const MINUS = '\u2212';
/** WORD JOINER: запрет переноса внутри единицы («маш.-ч/год» не рвётся на дефисе). */
const WJ = String.fromCharCode(0x2060);
/** Единицы с дефисом и косой чертой — неразрывно. */
const MACHINE_HOURS_YEAR = `маш.-${WJ}ч/${WJ}год`;
const MACHINE_HOURS = `маш.-${WJ}ч`;
const HOURS_MONTH = `ч/${WJ}мес`;

// ── Типографика ────────────────────────────────────────────────────────────

/** Короткие слова, после которых строка не должна обрываться. */
const SHORT_WORD =
  /(?<=^|[\s(«„"])(а|без|в|во|для|до|и|из|к|ко|на|над|не|ни|о|об|от|по|под|при|с|со|у|за)\s+/giu;

/**
 * Неразрывные пробелы для русского текста: после коротких предлогов и союзов,
 * перед тире. Применяется к подписям и пояснениям, которые переносятся.
 */
export function typo(text: string): string {
  return text.replace(SHORT_WORD, `$1${NBSP}`).replace(/\s+—/g, `${NBSP}—`);
}

// ── Десятичные строки ──────────────────────────────────────────────────────

const DECIMAL_RE = /^(-)?(\d+)(?:\.(\d+))?$/;

/** Значение ячейки как строка-число «8127935.03» или `null`, если это не число. */
export function decimalString(raw: unknown): string | null {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;
    const s = String(raw);
    // JSON-число могло прийти в экспоненте (1e-7) — раскрываем.
    return /e/i.test(s)
      ? raw.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 })
      : s;
  }
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  return DECIMAL_RE.test(s) ? s : null;
}

export interface DecimalFormat {
  minFraction: number;
  maxFraction: number;
  grouping: boolean;
}

/**
 * Число-строка → «8 127 935,03»: разряды неразрывным пробелом, запятая,
 * округление половины вверх — на строке, без float. Не число → `null`.
 */
export function formatDecimal(raw: string, format: DecimalFormat): string | null {
  const m = DECIMAL_RE.exec(raw.trim());
  if (!m) return null;
  let int = m[2];
  let frac = m[3] ?? '';
  if (frac.length > format.maxFraction) {
    const digits = (int + frac.slice(0, format.maxFraction)).split('').map(Number);
    if (Number(frac.charAt(format.maxFraction)) >= 5) {
      let i = digits.length - 1;
      while (i >= 0 && digits[i] === 9) {
        digits[i] = 0;
        i -= 1;
      }
      if (i >= 0) digits[i] += 1;
      else digits.unshift(1);
    }
    const all = digits.join('');
    int = all.slice(0, all.length - format.maxFraction);
    frac = all.slice(all.length - format.maxFraction);
  }
  int = int.replace(/^0+(?=\d)/, '');
  frac = frac.replace(/0+$/, '');
  while (frac.length < format.minFraction) frac += '0';
  const negative = m[1] === '-' && /[1-9]/.test(int + frac);
  const body = format.grouping ? int.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP) : int;
  return `${negative ? MINUS : ''}${body}${frac ? `,${frac}` : ''}`;
}

/**
 * Ввод человека → строка-число с точкой для API: «1 234,5» → «1234.5»,
 * «,5» → «0.5». Не число (буквы, «1,234.5» с двумя разделителями) → `null`.
 */
export function normalizeDecimal(input: string): string | null {
  let s = input.replace(/[\s\u00A0\u202F]/g, '');
  if (s === '') return null;
  s = s.replace(/^[\u2212\u2013]/, '-').replace(/^\+/, '');
  if ((s.match(/[.,]/g) ?? []).length > 1) return null;
  const m = /^(-?)(\d*)(?:[.,](\d*))?$/.exec(s);
  if (!m) return null;
  const sign = m[1];
  const intRaw = m[2];
  const frac = m[3] ?? '';
  if (intRaw === '' && frac === '') return null;
  const int = intRaw.replace(/^0+(?=\d)/, '') || '0';
  return `${sign}${int}${frac ? `.${frac}` : ''}`;
}

/** Деньги: «8 127 935,03»; пусто → «—». */
export function formatMoney(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  return formatDecimal(raw, { minFraction: 2, maxFraction: 2, grouping: true }) ?? raw;
}

/** Число в списке (единицы, количество): разряды, до трёх знаков после запятой. */
export function formatCount(n: number): string {
  return formatDecimal(String(n), { minFraction: 0, maxFraction: 3, grouping: true }) ?? String(n);
}

/** Дата и время правки: «20.10.2026, 16:01» по часам пользователя. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Статусы, действия журнала, источники ───────────────────────────────────

export const FLEET_STATUS_LABEL: Readonly<Record<FleetStatus, string>> = {
  active: 'В работе',
  reserve: 'В резерве',
  sold: 'Продана',
  written_off: 'Списана',
  archived: 'В архиве',
};

const STATUS_KEYS: ReadonlySet<string> = new Set(Object.keys(FLEET_STATUS_LABEL));

export function isFleetStatus(value: string): value is FleetStatus {
  return STATUS_KEYS.has(value);
}

/** Статусы, которые выбирают в форме модели (в архив — отдельной кнопкой). */
export const FLEET_MODEL_STATUSES: readonly FleetStatus[] = [
  'active',
  'reserve',
  'sold',
  'written_off',
];

/** У единицы отдельной кнопки архива нет — статус выбирают из всех. */
export const FLEET_UNIT_STATUSES: readonly FleetStatus[] = [
  'active',
  'reserve',
  'sold',
  'written_off',
  'archived',
];

export function fleetStatusLabel(status: string): string {
  return isFleetStatus(status) ? FLEET_STATUS_LABEL[status] : status;
}

/** Тон метки: цвет — только точка-маркер, текст всегда читается сам. */
export type FleetTone = 'success' | 'info' | 'neutral' | 'danger' | 'warning' | 'muted';

const STATUS_TONE: Readonly<Record<FleetStatus, FleetTone>> = {
  active: 'success',
  reserve: 'info',
  sold: 'neutral',
  written_off: 'danger',
  archived: 'muted',
};

export function fleetStatusTone(status: string): FleetTone {
  return isFleetStatus(status) ? STATUS_TONE[status] : 'neutral';
}

const ACTION_LABEL: Readonly<Record<FleetRevisionAction, string>> = {
  create: 'Создана',
  update: 'Изменена',
  archive: 'Отправлена в архив',
  restore: 'Возвращена из архива',
};

const ACTION_KEYS: ReadonlySet<string> = new Set(Object.keys(ACTION_LABEL));

function isRevisionAction(value: string): value is FleetRevisionAction {
  return ACTION_KEYS.has(value);
}

export function revisionActionLabel(action: string): string {
  return isRevisionAction(action) ? ACTION_LABEL[action] : action;
}

export function revisionSourceLabel(source: string): string {
  if (source === 'import') return 'импорт книги ЭММ';
  if (source === 'manual') return 'вручную';
  return source;
}

export function modelSourceLabel(source: string): string {
  if (source === 'import') return 'из книги ЭММ';
  if (source === 'manual') return 'внесена вручную';
  return source;
}

// ── Схема паспорта ─────────────────────────────────────────────────────────

export const RUB_NO_VAT = `₽${NBSP}без${NBSP}НДС`;

export type PassportBlockId =
  | 'identification'
  | 'tech'
  | 'owner'
  | 'cost'
  | 'mode'
  | 'repair'
  | 'tires'
  | 'operator'
  | 'fuel'
  | 'lubricants'
  | 'hydraulics';

export interface PassportBlock {
  id: PassportBlockId;
  title: string;
}

export const FLEET_PASSPORT_BLOCKS: readonly PassportBlock[] = [
  { id: 'identification', title: 'Идентификация' },
  { id: 'tech', title: 'Технические характеристики' },
  { id: 'owner', title: 'Картотека и собственник' },
  { id: 'cost', title: 'Стоимость и амортизация' },
  { id: 'mode', title: 'Режим работы' },
  { id: 'repair', title: 'Ремонт и ТО' },
  { id: 'tires', title: 'Шины' },
  { id: 'operator', title: 'Механизатор' },
  { id: 'fuel', title: 'Топливо' },
  { id: 'lubricants', title: 'Смазочные' },
  { id: 'hydraulics', title: 'Гидравлика и охлаждение' },
];

/** Подпись блока «Прочее» — поля, которых нет в схеме. */
export const OTHER_BLOCK_TITLE = 'Прочее';

/** Отображение числа: деньги — две цифры после запятой, год и № — как есть. */
export type NumberFormat = 'money' | 'pct' | 'coef' | 'decimal' | 'plain';

export interface AxisItem {
  key: string;
  label: string;
  /** Короткая подпись для заголовка колонки. */
  short?: string;
}

export const TEMPERATURE_ZONES: readonly AxisItem[] = [
  { key: 'I,II', label: 'I–II' },
  { key: 'IV', label: 'IV' },
  { key: 'V', label: 'V' },
  { key: 'VI', label: 'VI' },
  { key: 'VII', label: 'VII' },
];

/** Итоговая норма ремонта — по двум районам (зона VI и V книги). */
export const REPAIR_ZONES: readonly AxisItem[] = [
  { key: 'VI', label: 'Крайний Север' },
  { key: 'V', label: 'Остальная территория' },
];

export const OIL_KINDS: readonly AxisItem[] = [
  { key: 'motor', label: 'Моторные масла', short: 'Моторные' },
  {
    key: 'transmission',
    label: 'Трансмиссионные и гидравлические масла',
    short: 'Трансмиссионные и гидравлические',
  },
  { key: 'special', label: 'Специальные масла и жидкости', short: 'Специальные' },
  { key: 'grease', label: 'Пластичные смазки', short: 'Пластичные смазки' },
];

const SERVICE_YEARS: readonly string[] = ['2025', '2026', '2027', '2028', '2029', '2030'];
const OIL_ADJ_YEARS: readonly string[] = ['2026', '2027', '2028', '2029', '2030'];

interface FieldCommon {
  code: string;
  block: PassportBlockId;
  label: string;
  unit?: string;
  /**
   * Поле участвует в расчёте ПРЦ: его правка ставит модели «ПРЦ устарела».
   * Зеркало `osnova_core::prc::emm::PRC_INPUT_FIELDS`.
   */
  prc?: boolean;
}

export interface TextField extends FieldCommon {
  type: 'text';
  multiline?: boolean;
  /** Подсказки при вводе: из справочника (собственники, должности) или виды топлива. */
  suggest?: 'owner' | 'position' | 'fuel';
}

export interface NumberField extends FieldCommon {
  type: 'number';
  format: NumberFormat;
}

export interface ZonesField extends FieldCommon {
  type: 'zones';
  format: NumberFormat;
  axis: readonly AxisItem[];
  /** Подпись строки в таблице по зонам. */
  short: string;
}

export interface YearsField extends FieldCommon {
  type: 'years';
  format: NumberFormat;
  years: readonly string[];
  short: string;
  /** Показывается колонкой «Итого» в таблице поля «год × вид масла». */
  embedded?: boolean;
}

export interface OilKindsField extends FieldCommon {
  type: 'oilKinds';
  format: NumberFormat;
}

export interface YearOilKindsField extends FieldCommon {
  type: 'yearOilKinds';
  format: NumberFormat;
  years: readonly string[];
  /** Поле `years` с итогом по году — последняя колонка таблицы. */
  totalCode?: string;
}

export type ScalarField = TextField | NumberField;
export type DictField = ZonesField | YearsField | OilKindsField | YearOilKindsField;
export type PassportField = ScalarField | DictField;

function text(
  block: PassportBlockId,
  code: string,
  label: string,
  extra: Omit<Partial<TextField>, 'type' | 'code' | 'block' | 'label'> = {},
): TextField {
  return { type: 'text', block, code, label, ...extra };
}

function num(
  block: PassportBlockId,
  code: string,
  label: string,
  format: NumberFormat,
  extra: { unit?: string; prc?: boolean } = {},
): NumberField {
  return { type: 'number', block, code, label, format, ...extra };
}

function zones(
  block: PassportBlockId,
  code: string,
  label: string,
  format: NumberFormat,
  axis: readonly AxisItem[],
  short: string,
  extra: { unit?: string; prc?: boolean } = {},
): ZonesField {
  return { type: 'zones', block, code, label, format, axis, short, ...extra };
}

function years(
  block: PassportBlockId,
  code: string,
  label: string,
  format: NumberFormat,
  list: readonly string[],
  short: string,
  extra: { unit?: string; prc?: boolean; embedded?: boolean } = {},
): YearsField {
  return { type: 'years', block, code, label, format, years: list, short, ...extra };
}

/** Поля паспорта в порядке колонок листа «Справочник (паспорт техники)». */
export const FLEET_PASSPORT_FIELDS: readonly PassportField[] = [
  num('identification', 'row_no', '№ п/п', 'plain'),
  num('identification', 'units_count', 'Количество единиц', 'decimal'),
  text('identification', 'type_name', 'Наименование (тип ТС)'),
  text('identification', 'vehicle_category', 'Категория ТС'),
  num('identification', 'manufacture_year', 'Год выпуска', 'plain'),

  text('tech', 'extra_specs', 'Дополнительные характеристики', { multiline: true }),
  text('tech', 'dimensions', 'Габаритные размеры'),
  text('tech', 'engine_power', 'Мощность двигателя', { unit: `л.с.${NBSP}(кВт)` }),
  text('tech', 'engine_volume', 'Рабочий объём двигателя', { unit: `куб.${NBSP}см` }),
  text('tech', 'engine_type', 'Тип двигателя'),

  text('owner', 'owner', 'Собственник', { suggest: 'owner' }),
  text('owner', 'leasing_contract', 'Договор финансовой аренды (лизинга) №'),
  num('owner', 'contract_year', 'Год заключения договора, постановки на учёт', 'plain'),
  num('owner', 'leasing_term_months', 'Срок лизинговых платежей', 'decimal', { unit: 'мес' }),
  num(
    'owner',
    'leasing_total_cost',
    'Стоимость приобретения в лизинг с учётом всех платежей',
    'money',
    { unit: RUB_NO_VAT },
  ),
  num('owner', 'leasing_monthly_payment', 'Лизинговый платёж в месяц', 'money', { unit: '₽' }),
  num('owner', 'balance_term_years', 'Срок нахождения на балансе', 'decimal', { unit: 'лет' }),

  num('cost', 'current_cost', 'Текущая (восстановительная) стоимость', 'money', {
    unit: RUB_NO_VAT,
    prc: true,
  }),
  text('cost', 'amort_group', 'Амортизационная группа'),
  num('cost', 'amort_norm_pct', 'Норма амортизации', 'pct', { unit: '%' }),
  text('cost', 'asset_group', 'Группа и вид основных фондов'),
  num('cost', 'ka', 'Коэффициент интенсивности использования (Ка)', 'coef'),
  text('cost', 'ka_note', 'Пояснение к Ка', { multiline: true }),
  num('cost', 'amort_norm_with_ka_pct', 'Норма амортизации с учётом Ка', 'pct', {
    unit: '%',
    prc: true,
  }),
  num('cost', 'years_to_writeoff', 'Лет до полного списания', 'decimal'),

  num('mode', 'base_annual_hours', 'Годовой режим базисного района (III зона)', 'decimal', {
    unit: MACHINE_HOURS_YEAR,
    prc: true,
  }),
  zones(
    'mode',
    'zone_coef',
    'Поправочные коэффициенты по температурным зонам',
    'coef',
    TEMPERATURE_ZONES,
    'Поправочный коэффициент',
    { prc: true },
  ),
  zones(
    'mode',
    'annual_hours',
    'Годовой режим по зонам',
    'decimal',
    TEMPERATURE_ZONES,
    'Годовой режим',
    { unit: MACHINE_HOURS_YEAR },
  ),
  zones(
    'mode',
    'monthly_hours',
    'Месячный режим по зонам',
    'decimal',
    TEMPERATURE_ZONES,
    'Месячный режим',
    { unit: MACHINE_HOURS },
  ),

  num('repair', 'repair_norm_north_pct', 'Нормативный показатель — Крайний Север', 'pct', {
    unit: '%',
  }),
  num('repair', 'repair_norm_other_pct', 'Нормативный показатель — остальная территория', 'pct', {
    unit: '%',
  }),
  num('repair', 'repair_fact_coef', 'Коэффициент корректировки годовой нормы под факт', 'coef'),
  zones(
    'repair',
    'repair_norm_final',
    'Итоговая норма затрат на ремонт и ТО',
    'pct',
    REPAIR_ZONES,
    'Итоговая норма',
    { unit: '%', prc: true },
  ),

  num('tires', 'annual_mileage_km', 'Среднегодовой пробег', 'decimal', { unit: 'км', prc: true }),
  num('tires', 'tires_total', 'Шин всего с запаской', 'decimal', { unit: 'шт' }),
  text('tires', 'tire_front_size', 'Передние: типоразмер', { prc: true }),
  num('tires', 'tire_front_qty', 'Передние: количество', 'decimal', { prc: true }),
  text('tires', 'tire_rear_size', 'Задние: типоразмер', { prc: true }),
  num('tires', 'tire_rear_qty', 'Задние: количество', 'decimal', { prc: true }),
  text('tires', 'tire_spare_size', 'Запасные: типоразмер', { prc: true }),
  num('tires', 'tire_spare_qty', 'Запасные: количество', 'decimal', { prc: true }),
  num('tires', 'tire_norm_mileage_km', 'Норма эксплуатационного пробега', 'decimal', {
    unit: 'км',
  }),
  num('tires', 'tire_avg_mileage_km', 'Среднестатистический пробег шины', 'decimal', {
    unit: 'км',
    prc: true,
  }),
  num('tires', 'tire_k1', 'К1 — категория условий эксплуатации', 'coef', { prc: true }),
  num('tires', 'tire_k2', 'К2 — условия работы', 'coef', { prc: true }),
  text('tires', 'tire_condition_category', 'Категория условий эксплуатации'),
  text('tires', 'tire_work_conditions', 'Рабочие условия', { multiline: true }),

  num('operator', 'work_hours_month', 'Норма рабочего времени', 'decimal', { unit: HOURS_MONTH }),
  text('operator', 'operator_position', 'Должность водителя/механизатора', {
    prc: true,
    suggest: 'position',
  }),

  num('fuel', 'fuel_per_100km', 'Норма расхода на 100 км', 'decimal', { unit: 'л', prc: true }),
  num('fuel', 'fuel_per_motor_hour', 'Норма на моточас', 'decimal', { unit: 'л', prc: true }),
  num('fuel', 'fuel_equipment_per_hour', 'Норма на работу оборудования', 'decimal', {
    unit: 'л/ч',
    prc: true,
  }),
  text('fuel', 'fuel_kind', 'Вид топлива', { prc: true, suggest: 'fuel' }),

  {
    type: 'oilKinds',
    block: 'lubricants',
    code: 'oil_norm',
    label: 'Нормы на 100 л топлива',
    format: 'decimal',
  },
  num('lubricants', 'oil_base_year', 'Год выпуска', 'plain'),
  years(
    'lubricants',
    'service_years',
    'Срок эксплуатации на период',
    'decimal',
    SERVICE_YEARS,
    'Срок эксплуатации',
    { unit: 'лет' },
  ),
  years(
    'lubricants',
    'oil_coef',
    'Поправочный коэффициент на период',
    'coef',
    SERVICE_YEARS,
    'Поправочный коэффициент',
  ),
  {
    type: 'yearOilKinds',
    block: 'lubricants',
    code: 'oil_norm_adj',
    label: 'Нормы с поправочным коэффициентом по годам',
    format: 'decimal',
    years: OIL_ADJ_YEARS,
    totalCode: 'oil_norm_adj_total',
    prc: true,
  },
  years(
    'lubricants',
    'oil_norm_adj_total',
    'Итого норма на 100 л топлива с коэффициентом',
    'decimal',
    OIL_ADJ_YEARS,
    'Итого',
    { embedded: true },
  ),

  num('hydraulics', 'hydraulic_capacity_l', 'Ёмкость гидросистемы', 'decimal', {
    unit: 'л',
    prc: true,
  }),
  num('hydraulics', 'hydraulic_density', 'Плотность гидрожидкости', 'coef', { prc: true }),
  num('hydraulics', 'hydraulic_topup_coef', 'Коэффициент доливок', 'coef', { prc: true }),
  num('hydraulics', 'hydraulic_replacement_period', 'Периодичность полной замены', 'decimal', {
    prc: true,
  }),
  num('hydraulics', 'coolant_monthly_l', 'Месячный расход охлаждающей жидкости', 'decimal', {
    unit: 'л',
    prc: true,
  }),
];

const FIELD_BY_CODE: ReadonlyMap<string, PassportField> = new Map(
  FLEET_PASSPORT_FIELDS.map((f) => [f.code, f]),
);
const FIELD_ORDER: ReadonlyMap<string, number> = new Map(
  FLEET_PASSPORT_FIELDS.map((f, i) => [f.code, i]),
);
const BLOCK_TITLE: ReadonlyMap<string, string> = new Map(
  FLEET_PASSPORT_BLOCKS.map((b) => [b.id, b.title]),
);

export function passportField(code: string): PassportField | undefined {
  return FIELD_BY_CODE.get(code);
}

export function isDictField(f: PassportField): f is DictField {
  return f.type !== 'text' && f.type !== 'number';
}

/** Подпись поля с единицей измерения: «Срок лизинговых платежей, мес». */
export function fieldLabel(f: Pick<FieldCommon, 'label' | 'unit'>): string {
  return typo(f.unit ? `${f.label}, ${f.unit}` : f.label);
}

function shortLabel(f: ZonesField | YearsField): string {
  return typo(f.unit ? `${f.short}, ${f.unit}` : f.short);
}

// ── Значения ───────────────────────────────────────────────────────────────

export function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
}

/** Значение ячейки текстом — для текстовых полей и непонятных значений. */
export function scalarText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return JSON.stringify(v);
}

const NUMBER_FORMATS: Readonly<Record<Exclude<NumberFormat, 'plain'>, DecimalFormat>> = {
  money: { minFraction: 2, maxFraction: 2, grouping: true },
  pct: { minFraction: 0, maxFraction: 2, grouping: true },
  coef: { minFraction: 0, maxFraction: 4, grouping: true },
  decimal: { minFraction: 0, maxFraction: 3, grouping: true },
};

const EXACT: DecimalFormat = { minFraction: 0, maxFraction: 40, grouping: true };

/** Отображаемое значение и точное — если показ округлил число. */
export interface ViewCell {
  text: string;
  exact: string | null;
}

/** Число-строка в формате поля; `exact` — полная запись, если показ её округлил. */
export function formatNumberCell(raw: unknown, format: NumberFormat): ViewCell | null {
  if (isEmpty(raw)) return null;
  const d = decimalString(raw);
  if (d === null) return { text: scalarText(raw), exact: null };
  if (format === 'plain') {
    return { text: d.replace('.', ',').replace(/^-/, MINUS), exact: null };
  }
  const shown = formatDecimal(d, NUMBER_FORMATS[format]) ?? d;
  const full = formatDecimal(d, EXACT) ?? d;
  // «12,00» и «12» — одно число: точное показываем, только если цифры отличаются.
  const same = shown.replace(/,?0+$/, '') === full.replace(/,?0+$/, '');
  return { text: shown, exact: same ? null : full };
}

function cellOf(f: PassportField, raw: unknown): ViewCell | null {
  if (isEmpty(raw)) return null;
  if (f.type === 'text') return { text: scalarText(raw).trim(), exact: null };
  return formatNumberCell(raw, f.format);
}

// ── Паспорт для просмотра ──────────────────────────────────────────────────

export interface MatrixColumn {
  key: string;
  label: string;
}

export type PassportViewItem =
  | {
      kind: 'field';
      code: string;
      label: string;
      value: ViewCell;
      multiline: boolean;
    }
  | {
      kind: 'matrix';
      id: string;
      caption: string;
      corner: string;
      columns: readonly MatrixColumn[];
      rows: readonly { code: string; label: string; cells: readonly (ViewCell | null)[] }[];
    }
  | {
      kind: 'pairs';
      code: string;
      label: string;
      pairs: readonly { key: string; label: string; value: ViewCell }[];
    };

export interface PassportViewBlock {
  id: PassportBlockId | 'other';
  title: string;
  items: PassportViewItem[];
}

function sortYears(keys: Iterable<string>): string[] {
  return Array.from(new Set(keys)).sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }));
}

/** Колонки поля по оси: из схемы плюс ключи, которые есть в данных. */
function axisColumns(
  f: ZonesField | YearsField,
  dicts: readonly Record<string, unknown>[],
): MatrixColumn[] {
  const present = dicts.flatMap((d) => Object.keys(d));
  if (f.type === 'zones') {
    const known = new Set(f.axis.map((a) => a.key));
    const extra = Array.from(new Set(present.filter((k) => !known.has(k))));
    return [
      ...f.axis.map((a) => ({ key: a.key, label: a.label })),
      ...extra.map((k) => ({ key: k, label: k })),
    ];
  }
  return sortYears([...f.years, ...present]).map((y) => ({ key: y, label: y }));
}

function sameAxis(a: PassportField, b: PassportField): boolean {
  if (a.type === 'zones' && b.type === 'zones') {
    return a.axis.map((x) => x.key).join('|') === b.axis.map((x) => x.key).join('|');
  }
  if (a.type === 'years' && b.type === 'years') {
    return !a.embedded && !b.embedded && a.years.join('|') === b.years.join('|');
  }
  return false;
}

function dictOf(passport: Record<string, unknown>, code: string): Record<string, unknown> {
  const v = passport[code];
  return isRecord(v) ? v : {};
}

function axisCaption(f: ZonesField | YearsField): string {
  if (f.type === 'years') return 'По годам';
  return f.axis === REPAIR_ZONES ? 'По районам' : 'По температурным зонам';
}

/** Год и вид масла из плоского ключа «2026.motor». */
function splitYearKind(key: string): { year: string; kind: string } | null {
  const m = /^(\d{4})\.(.+)$/.exec(key);
  return m ? { year: m[1], kind: m[2] } : null;
}

function oilKindColumns(present: Iterable<string>): MatrixColumn[] {
  const known = new Set(OIL_KINDS.map((k) => k.key));
  const extra = Array.from(new Set(Array.from(present).filter((k) => !known.has(k))));
  return [
    ...OIL_KINDS.map((k) => ({ key: k.key, label: k.short ?? k.label })),
    ...extra.map((k) => ({ key: k, label: k })),
  ];
}

function viewItemsForBlock(
  fields: readonly PassportField[],
  passport: Record<string, unknown>,
): PassportViewItem[] {
  const items: PassportViewItem[] = [];
  for (let i = 0; i < fields.length; i += 1) {
    const f = fields[i];
    if (f.type === 'text' || f.type === 'number') {
      const value = cellOf(f, passport[f.code]);
      if (value) {
        items.push({
          kind: 'field',
          code: f.code,
          label: fieldLabel(f),
          value,
          multiline: f.type === 'text' && f.multiline === true,
        });
      }
      continue;
    }
    if (f.type === 'zones' || f.type === 'years') {
      if (f.type === 'years' && f.embedded) continue;
      const group: (ZonesField | YearsField)[] = [f];
      while (i + 1 < fields.length && sameAxis(f, fields[i + 1])) {
        const next = fields[i + 1];
        if (next.type !== 'zones' && next.type !== 'years') break;
        group.push(next);
        i += 1;
      }
      const dicts = group.map((g) => dictOf(passport, g.code));
      const columns = axisColumns(f, dicts);
      const rows = group
        .map((g, gi) => ({
          code: g.code,
          label: shortLabel(g),
          cells: columns.map((c) => cellOf(g, dicts[gi][c.key])),
        }))
        .filter((r) => r.cells.some((c) => c !== null));
      if (rows.length > 0) {
        items.push({
          kind: 'matrix',
          id: group.map((g) => g.code).join('+'),
          caption: group.length === 1 ? fieldLabel(f) : axisCaption(f),
          corner: 'Показатель',
          columns,
          rows,
        });
      }
      continue;
    }
    if (f.type === 'oilKinds') {
      const dict = dictOf(passport, f.code);
      const known = new Set(OIL_KINDS.map((k) => k.key));
      const axis = [
        ...OIL_KINDS,
        ...Object.keys(dict)
          .filter((k) => !known.has(k))
          .map((k) => ({ key: k, label: k })),
      ];
      const pairs = axis.flatMap((k) => {
        const value = cellOf(f, dict[k.key]);
        return value ? [{ key: k.key, label: k.label, value }] : [];
      });
      if (pairs.length > 0)
        items.push({ kind: 'pairs', code: f.code, label: fieldLabel(f), pairs });
      continue;
    }
    // yearOilKinds: строки — годы, колонки — виды масел и «Итого».
    const dict = dictOf(passport, f.code);
    const total = f.totalCode ? FIELD_BY_CODE.get(f.totalCode) : undefined;
    const totalDict = total ? dictOf(passport, total.code) : {};
    const parsed = Object.keys(dict)
      .map(splitYearKind)
      .filter((p) => p !== null);
    const yearsList = sortYears([
      ...f.years,
      ...parsed.map((p) => p.year),
      ...Object.keys(totalDict),
    ]);
    const columns = oilKindColumns(parsed.map((p) => p.kind));
    // Колонка «Итого» — только если итоги в паспорте есть: столбец прочерков не нужен.
    const withTotal =
      total && Object.keys(totalDict).length > 0
        ? [...columns, { key: '__total', label: total.type === 'years' ? total.short : 'Итого' }]
        : columns;
    const rows = yearsList
      .map((y) => ({
        code: f.code,
        label: y,
        cells: withTotal.map((c) =>
          c.key === '__total' && total
            ? cellOf(total, totalDict[y])
            : cellOf(f, dict[`${y}.${c.key}`]),
        ),
      }))
      .filter((r) => r.cells.some((c) => c !== null));
    if (rows.length > 0) {
      items.push({
        kind: 'matrix',
        id: f.code,
        caption: fieldLabel(f),
        corner: 'Год',
        columns: withTotal,
        rows,
      });
    }
  }
  return items;
}

/**
 * Паспорт по блокам для карточки: только заполненные поля; поля по зонам и
 * годам соседними строками сводятся в одну таблицу. Неизвестные коды — в
 * блок «Прочее» с кодом вместо подписи.
 */
export function buildPassportView(passport: Record<string, unknown>): PassportViewBlock[] {
  const blocks: PassportViewBlock[] = FLEET_PASSPORT_BLOCKS.map((b) => ({
    id: b.id,
    title: b.title,
    items: viewItemsForBlock(
      FLEET_PASSPORT_FIELDS.filter((f) => f.block === b.id),
      passport,
    ),
  }));
  const other = Object.keys(passport)
    .filter((k) => !FIELD_BY_CODE.has(k) && !isEmpty(passport[k]))
    .sort();
  if (other.length > 0) {
    blocks.push({
      id: 'other',
      title: OTHER_BLOCK_TITLE,
      items: other.map((code): PassportViewItem => {
        const v = passport[code];
        if (isRecord(v)) {
          return {
            kind: 'pairs',
            code,
            label: code,
            pairs: Object.keys(v)
              .filter((k) => !isEmpty(v[k]))
              .map((k) => ({ key: k, label: k, value: { text: scalarText(v[k]), exact: null } })),
          };
        }
        return {
          kind: 'field',
          code,
          label: code,
          value: { text: scalarText(v), exact: null },
          multiline: false,
        };
      }),
    });
  }
  return blocks;
}

// ── Форма ──────────────────────────────────────────────────────────────────

/** Значения полей формы строками. Ключи: `name`, `category`, …, `p|код`, `p|код|подключ`. */
export type FormValues = Readonly<Record<string, string>>;

export const FORM_KEY = {
  name: 'name',
  category: 'category',
  units: 'units_count',
  status: 'status',
  comment: 'comment',
} as const;

const SEP = '|';

export function cellKey(code: string, sub?: string): string {
  if (code === 'units_count' && sub === undefined) return FORM_KEY.units;
  return sub === undefined ? `p${SEP}${code}` : `p${SEP}${code}${SEP}${sub}`;
}

/** Ячейка формы по ключу: поле схемы и подключ (зона, год, вид масла). */
function parseCellKey(key: string): { field: PassportField; sub: string | null } | null {
  if (key === FORM_KEY.units) {
    const f = FIELD_BY_CODE.get('units_count');
    return f ? { field: f, sub: null } : null;
  }
  if (!key.startsWith(`p${SEP}`)) return null;
  const rest = key.slice(2);
  const at = rest.indexOf(SEP);
  const code = at < 0 ? rest : rest.slice(0, at);
  const f = FIELD_BY_CODE.get(code);
  if (!f) return null;
  return { field: f, sub: at < 0 ? null : rest.slice(at + 1) };
}

function toInput(raw: unknown, numeric: boolean): string {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'string') {
    const t = raw.trim();
    return numeric && DECIMAL_RE.test(t) ? t.replace('.', ',') : raw;
  }
  if (typeof raw === 'number') {
    const d = decimalString(raw);
    return d === null ? String(raw) : d.replace('.', ',');
  }
  return scalarText(raw);
}

/** Подключи поля-словаря: из схемы и из данных. Значения-словари не редактируются. */
function dictSubKeys(f: DictField, dict: Record<string, unknown>): string[] {
  const scalarKeys = Object.keys(dict).filter((k) => !isRecord(dict[k]) && !Array.isArray(dict[k]));
  if (f.type === 'zones') {
    const known = f.axis.map((a) => a.key);
    return [...known, ...scalarKeys.filter((k) => !known.includes(k))];
  }
  if (f.type === 'years') return sortYears([...f.years, ...scalarKeys]);
  if (f.type === 'oilKinds') {
    const known = OIL_KINDS.map((k) => k.key);
    return [...known, ...scalarKeys.filter((k) => !known.includes(k))];
  }
  const parsed = scalarKeys.map(splitYearKind).filter((p) => p !== null);
  const yearsList = sortYears([...f.years, ...parsed.map((p) => p.year)]);
  const kinds = oilKindColumns(parsed.map((p) => p.kind)).map((c) => c.key);
  const grid = yearsList.flatMap((y) => kinds.map((k) => `${y}.${k}`));
  // Ключи не по шаблону «год.вид» тоже остаются ячейками — в конце.
  return [...grid, ...scalarKeys.filter((k) => !grid.includes(k))];
}

/** Начальные значения формы из модели; `null` — новая модель. */
export function initialFormValues(model: FleetModel | null): Record<string, string> {
  const values: Record<string, string> = {
    [FORM_KEY.name]: model?.name ?? '',
    [FORM_KEY.category]: model?.category_name ?? '',
    [FORM_KEY.units]: model ? String(model.units_count) : '1',
    [FORM_KEY.status]: model?.status ?? 'active',
    [FORM_KEY.comment]: model?.comment ?? '',
  };
  const passport = model?.passport ?? {};
  for (const f of FLEET_PASSPORT_FIELDS) {
    if (f.code === 'units_count') continue;
    if (isDictField(f)) {
      const dict = dictOf(passport, f.code);
      for (const sub of dictSubKeys(f, dict))
        values[cellKey(f.code, sub)] = toInput(dict[sub], true);
    } else {
      values[cellKey(f.code)] = toInput(passport[f.code], f.type === 'number');
    }
  }
  return values;
}

export interface FormCell {
  key: string;
  label: string;
  numeric: boolean;
  prc: boolean;
}

export type PassportFormItem =
  | { kind: 'field'; key: string; field: ScalarField; label: string }
  | {
      kind: 'matrix';
      id: string;
      caption: string;
      corner: string;
      columns: readonly MatrixColumn[];
      rows: readonly { label: string; prc: boolean; cells: readonly (FormCell | null)[] }[];
    }
  | { kind: 'pairs'; code: string; label: string; prc: boolean; cells: readonly FormCell[] };

export interface PassportFormBlock {
  id: PassportBlockId;
  title: string;
  items: PassportFormItem[];
}

/** Подключи поля, для которых в форме есть ячейки. */
function formSubKeys(values: FormValues, code: string): string[] {
  const prefix = `p${SEP}${code}${SEP}`;
  return Object.keys(values)
    .filter((k) => k.startsWith(prefix))
    .map((k) => k.slice(prefix.length));
}

function formItemsForBlock(
  fields: readonly PassportField[],
  values: FormValues,
): PassportFormItem[] {
  const items: PassportFormItem[] = [];
  for (let i = 0; i < fields.length; i += 1) {
    const f = fields[i];
    if (f.type === 'text' || f.type === 'number') {
      items.push({ kind: 'field', key: cellKey(f.code), field: f, label: fieldLabel(f) });
      continue;
    }
    if (f.type === 'zones' || f.type === 'years') {
      if (f.type === 'years' && f.embedded) continue;
      const group: (ZonesField | YearsField)[] = [f];
      while (i + 1 < fields.length && sameAxis(f, fields[i + 1])) {
        const next = fields[i + 1];
        if (next.type !== 'zones' && next.type !== 'years') break;
        group.push(next);
        i += 1;
      }
      const present = group.map((g) =>
        Object.fromEntries(formSubKeys(values, g.code).map((s) => [s, true])),
      );
      const columns = axisColumns(f, present);
      items.push({
        kind: 'matrix',
        id: group.map((g) => g.code).join('+'),
        caption: group.length === 1 ? fieldLabel(f) : axisCaption(f),
        corner: 'Показатель',
        columns,
        rows: group.map((g) => ({
          label: shortLabel(g),
          prc: g.prc === true,
          cells: columns.map((c) => {
            const key = cellKey(g.code, c.key);
            return key in values
              ? { key, label: `${fieldLabel(g)} — ${c.label}`, numeric: true, prc: g.prc === true }
              : null;
          }),
        })),
      });
      continue;
    }
    if (f.type === 'oilKinds') {
      const subs = formSubKeys(values, f.code);
      const known = new Map(OIL_KINDS.map((k) => [k.key, k.label]));
      items.push({
        kind: 'pairs',
        code: f.code,
        label: fieldLabel(f),
        prc: f.prc === true,
        cells: subs.map((s) => ({
          key: cellKey(f.code, s),
          label: known.get(s) ?? s,
          numeric: true,
          prc: f.prc === true,
        })),
      });
      continue;
    }
    const subs = formSubKeys(values, f.code);
    const total = f.totalCode ? FIELD_BY_CODE.get(f.totalCode) : undefined;
    const parsed = subs.map(splitYearKind).filter((p) => p !== null);
    const totalYears = total ? formSubKeys(values, total.code) : [];
    const yearsList = sortYears([...parsed.map((p) => p.year), ...totalYears]);
    const columns = oilKindColumns(parsed.map((p) => p.kind));
    const withTotal = total
      ? [...columns, { key: '__total', label: total.type === 'years' ? total.short : 'Итого' }]
      : columns;
    items.push({
      kind: 'matrix',
      id: f.code,
      caption: fieldLabel(f),
      corner: 'Год',
      columns: withTotal,
      rows: yearsList.map((y) => ({
        label: y,
        prc: f.prc === true,
        cells: withTotal.map((c) => {
          if (c.key === '__total' && total) {
            const key = cellKey(total.code, y);
            return key in values
              ? {
                  key,
                  label: `${fieldLabel(total)} — ${y}`,
                  numeric: true,
                  prc: total.prc === true,
                }
              : null;
          }
          const key = cellKey(f.code, `${y}.${c.key}`);
          return key in values
            ? {
                key,
                label: `${fieldLabel(f)} — ${y}, ${c.label}`,
                numeric: true,
                prc: f.prc === true,
              }
            : null;
        }),
      })),
    });
  }
  return items;
}

/** Раскладка формы паспорта по блокам — из той же схемы, что и карточка. */
export function buildPassportForm(values: FormValues): PassportFormBlock[] {
  return FLEET_PASSPORT_BLOCKS.map((b) => ({
    id: b.id,
    title: b.title,
    items: formItemsForBlock(
      FLEET_PASSPORT_FIELDS.filter((f) => f.block === b.id),
      values,
    ),
  }));
}

/** Поля паспорта не из схемы — форма их показывает и сохраняет как есть. */
export function unknownPassportEntries(passport: Record<string, unknown>): [string, string][] {
  return Object.keys(passport)
    .filter((k) => !FIELD_BY_CODE.has(k))
    .sort()
    .map((k) => [k, scalarText(passport[k])]);
}

/** Ключи, которые человек изменил. */
export function changedKeys(values: FormValues, initial: FormValues): string[] {
  const keys = new Set([...Object.keys(values), ...Object.keys(initial)]);
  return Array.from(keys).filter((k) => (values[k] ?? '') !== (initial[k] ?? ''));
}

export interface FormIssue {
  key: string;
  message: string;
}

export const NUMBER_HINT = 'Нужно число, например 12,5';
const UNITS_HINT = 'Целое число, 0 или больше';
const NAME_HINT = 'Укажите марку и модель — это ключ модели в справочнике';

/**
 * Значение ячейки для тела запроса. Нетронутое поле уходит РОВНО тем
 * значением, что пришло с сервера: иначе «626.4000000000001» после правки
 * соседнего поля превратился бы в «626.4» и попал в журнал как изменение
 * (а в поле ПРЦ — ещё и пометил бы цену устаревшей).
 */
function cellPayload(
  numeric: boolean,
  current: string,
  initial: string,
  original: unknown,
): { value: unknown } | { error: string } | null {
  if (current === initial) return isEmpty(original) ? null : { value: original };
  const t = current.trim();
  if (t === '') return null;
  if (!numeric) return { value: t };
  const n = normalizeDecimal(t);
  return n === null ? { error: NUMBER_HINT } : { value: n };
}

/**
 * Тело `POST`/`PUT /fleet/models` из значений формы и ошибки проверки.
 * `base` — модель, из которой построены `initial` (её паспорт — источник
 * нетронутых значений и полей не из схемы).
 */
export function buildModelPayload(
  values: FormValues,
  initial: FormValues,
  base: FleetModel | null,
): { data: FleetModelData; issues: FormIssue[] } {
  const issues: FormIssue[] = [];
  const original = base?.passport ?? {};
  const passport: Record<string, unknown> = {};

  // Ошибки — в порядке формы: сначала наименование и число единиц (они выше
  // паспорта), чтобы «к первой ошибке» вело к верхней.
  const name = (values[FORM_KEY.name] ?? '').trim();
  if (name === '') issues.push({ key: FORM_KEY.name, message: NAME_HINT });
  const unitsRaw = (values[FORM_KEY.units] ?? '').replace(/[\s\u00A0]/g, '');
  const unitsOk = /^\d{1,6}$/.test(unitsRaw);
  if (!unitsOk) issues.push({ key: FORM_KEY.units, message: UNITS_HINT });
  const units = unitsOk ? Number(unitsRaw) : 0;

  // Поля не из схемы — как пришли.
  for (const [k, v] of Object.entries(original)) {
    if (!FIELD_BY_CODE.has(k)) passport[k] = v;
  }

  for (const f of FLEET_PASSPORT_FIELDS) {
    if (f.code === 'units_count') continue;
    if (!isDictField(f)) {
      const key = cellKey(f.code);
      const res = cellPayload(
        f.type === 'number',
        values[key] ?? '',
        initial[key] ?? '',
        original[f.code],
      );
      if (res && 'error' in res) issues.push({ key, message: res.error });
      else if (res) passport[f.code] = res.value;
      continue;
    }
    const origValue = original[f.code];
    const origDict = isRecord(origValue) ? origValue : {};
    const subs = formSubKeys(values, f.code);
    const dirty = subs.some(
      (s) => (values[cellKey(f.code, s)] ?? '') !== (initial[cellKey(f.code, s)] ?? ''),
    );
    if (!dirty && origValue !== undefined && !isRecord(origValue)) {
      // Не словарь там, где схема ждёт словарь: не трогаем, пока не правили.
      passport[f.code] = origValue;
      continue;
    }
    const dict: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(origDict)) {
      if (!subs.includes(k)) dict[k] = v; // вложенные значения без ячейки формы
    }
    for (const s of subs) {
      const key = cellKey(f.code, s);
      const res = cellPayload(true, values[key] ?? '', initial[key] ?? '', origDict[s]);
      if (res && 'error' in res) issues.push({ key, message: res.error });
      else if (res) dict[s] = res.value;
    }
    if (Object.keys(dict).length > 0) passport[f.code] = dict;
  }

  // Число единиц — колонка модели; в паспорте то же значение, как в книге.
  const unitsDirty = (values[FORM_KEY.units] ?? '') !== (initial[FORM_KEY.units] ?? '');
  if (unitsOk && (unitsDirty || base === null)) passport.units_count = String(units);
  else if (!isEmpty(original.units_count)) passport.units_count = original.units_count;

  const statusRaw = values[FORM_KEY.status] ?? 'active';
  const status: FleetStatus = isFleetStatus(statusRaw) ? statusRaw : 'active';
  const category = (values[FORM_KEY.category] ?? '').trim();
  const comment = (values[FORM_KEY.comment] ?? '').trim();

  const data: FleetModelData = {
    name,
    category_name: category === '' ? null : category,
    units_count: units,
    status,
    passport,
    comment: comment === '' ? null : comment,
  };
  return { data, issues };
}

/** Подпись ключа формы — для сводки ошибок. */
export function formKeyLabel(key: string): string {
  switch (key) {
    case FORM_KEY.name:
      return 'Марка, модель';
    case FORM_KEY.category:
      return 'Категория техники';
    case FORM_KEY.units:
      return 'Количество единиц';
    case FORM_KEY.status:
      return 'Статус';
    case FORM_KEY.comment:
      return 'Комментарий';
    default: {
      const parsed = parseCellKey(key);
      if (!parsed) return key;
      const base = fieldLabel(parsed.field);
      return parsed.sub === null ? base : `${base} — ${subKeyLabel(parsed.field, parsed.sub)}`;
    }
  }
}

/** Блок, в котором лежит поле формы (для перехода к ошибке). */
export function formKeyBlock(key: string): PassportBlockId | null {
  return parseCellKey(key)?.field.block ?? null;
}

/**
 * Перечитать модель после 409: свежая версия становится основой формы, а
 * правки человека переносятся поверх. `conflicts` — ключи, которые изменили
 * и он, и тот, кто сохранил раньше: их показываем отдельно.
 */
export function rebaseForm(
  values: FormValues,
  initial: FormValues,
  fresh: FleetModel,
): { values: Record<string, string>; initial: Record<string, string>; conflicts: string[] } {
  const freshInitial = initialFormValues(fresh);
  const merged: Record<string, string> = { ...freshInitial };
  const conflicts: string[] = [];
  for (const key of changedKeys(values, initial)) {
    if ((freshInitial[key] ?? '') !== (initial[key] ?? '')) conflicts.push(key);
    merged[key] = values[key] ?? '';
  }
  return { values: merged, initial: freshInitial, conflicts };
}

// ── «Было → стало» ─────────────────────────────────────────────────────────

const SPECIAL_DIFF: Readonly<Record<string, string>> = {
  name: 'Марка, модель',
  category: 'Категория техники',
  units_count: 'Количество единиц',
  status: 'Статус',
  comment: 'Комментарий',
};

function subKeyLabel(f: PassportField, sub: string): string {
  if (f.type === 'zones') return f.axis.find((a) => a.key === sub)?.label ?? sub;
  if (f.type === 'oilKinds')
    return (OIL_KINDS.find((k) => k.key === sub)?.label ?? sub).toLowerCase();
  if (f.type === 'yearOilKinds') {
    const p = splitYearKind(sub);
    if (!p) return sub;
    const kind = OIL_KINDS.find((k) => k.key === p.kind)?.label ?? p.kind;
    return `${p.year}, ${kind.toLowerCase()}`;
  }
  return sub;
}

export interface DiffRow {
  key: string;
  /** Блок паспорта — чтобы «Год выпуска» смазочных не спутать с годом выпуска машины. */
  block: string | null;
  label: string;
  before: string;
  after: string;
  prc: boolean;
}

function diffKeyInfo(key: string): {
  block: string | null;
  label: string;
  field: PassportField | null;
} {
  const special = SPECIAL_DIFF[key];
  if (special) return { block: null, label: special, field: null };
  const dot = key.indexOf('.');
  const code = dot < 0 ? key : key.slice(0, dot);
  const f = FIELD_BY_CODE.get(code);
  if (!f)
    return {
      block: OTHER_BLOCK_TITLE,
      label: dot < 0 ? key : `${code} — ${key.slice(dot + 1)}`,
      field: null,
    };
  const base = fieldLabel(f);
  return {
    block: BLOCK_TITLE.get(f.block) ?? null,
    label: dot < 0 ? base : typo(`${base} — ${subKeyLabel(f, key.slice(dot + 1))}`),
    field: f,
  };
}

/** Значение «было»/«стало» текстом: статус — подписью, числа — в формате поля. */
export function diffValueText(key: string, value: unknown): string {
  if (isEmpty(value)) return '—';
  if (key === 'status' && typeof value === 'string') return fleetStatusLabel(value);
  if (key === 'units_count') return formatNumberCell(value, 'decimal')?.text ?? scalarText(value);
  const { field } = diffKeyInfo(key);
  if (field && field.type !== 'text') {
    return formatNumberCell(value, field.format)?.text ?? scalarText(value);
  }
  return scalarText(value);
}

function diffOrder(key: string): number {
  const specialIndex = Object.keys(SPECIAL_DIFF).indexOf(key);
  if (specialIndex >= 0) return specialIndex;
  const code = key.split('.')[0];
  const i = FIELD_ORDER.get(code);
  return i === undefined ? 100_000 : 100 + i;
}

/** Строки «было → стало» в порядке паспорта, с подписями из схемы. */
export function diffRows(diff: Readonly<Record<string, FleetDiffEntry>>): DiffRow[] {
  return Object.keys(diff)
    .sort((a, b) => diffOrder(a) - diffOrder(b) || a.localeCompare(b, 'ru', { numeric: true }))
    .map((key) => {
      const entry = diff[key];
      const info = diffKeyInfo(key);
      return {
        key,
        block: info.block,
        label: info.label,
        before: diffValueText(key, entry?.before),
        after: diffValueText(key, entry?.after),
        prc: info.field?.prc === true,
      };
    });
}

// ── Список моделей ─────────────────────────────────────────────────────────

export interface FleetModelGroup {
  key: string;
  title: string;
  models: FleetModel[];
  units: number;
}

export const NO_CATEGORY_TITLE = 'Без категории';

/** Группы по категории в порядке списка (backend уже упорядочил по `sort_order`). */
export function groupModelsByCategory(models: readonly FleetModel[]): FleetModelGroup[] {
  const groups: FleetModelGroup[] = [];
  const byKey = new Map<string, FleetModelGroup>();
  for (const m of models) {
    const key = m.category_id ?? '__none';
    let g = byKey.get(key);
    if (!g) {
      g = { key, title: m.category_name ?? NO_CATEGORY_TITLE, models: [], units: 0 };
      byKey.set(key, g);
      groups.push(g);
    }
    g.models.push(m);
    g.units += m.units_count;
  }
  return groups;
}

export type FleetSortKey = 'name' | 'category' | 'units' | 'cost' | 'prc';
export type SortDirection = 'asc' | 'desc';

/** Число только для порядка строк — в расчёты не идёт. */
function sortNumber(raw: string | null | undefined): number | null {
  const d = raw === null || raw === undefined ? null : decimalString(raw);
  return d === null ? null : Number(d);
}

function costNumber(m: FleetModel): number | null {
  return sortNumber(m.current_cost);
}

/** Сортировка плоского списка; пустая стоимость и неутверждённая ПРЦ — всегда в конце. */
export function sortFleetModels(
  models: readonly FleetModel[],
  key: FleetSortKey,
  dir: SortDirection,
): FleetModel[] {
  const sign = dir === 'asc' ? 1 : -1;
  const text = (a: string, b: string) =>
    a.localeCompare(b, 'ru', { numeric: true, sensitivity: 'base' });
  return [...models].sort((a, b) => {
    switch (key) {
      case 'name':
        return sign * text(a.name, b.name);
      case 'category':
        return sign * text(a.category_name ?? '', b.category_name ?? '') || text(a.name, b.name);
      case 'units':
        return sign * (a.units_count - b.units_count) || text(a.name, b.name);
      case 'cost':
      case 'prc': {
        const x = key === 'cost' ? costNumber(a) : sortNumber(a.prc?.price);
        const y = key === 'cost' ? costNumber(b) : sortNumber(b.prc?.price);
        if (x === null && y === null) return text(a.name, b.name);
        if (x === null) return 1;
        if (y === null) return -1;
        return sign * (x - y) || text(a.name, b.name);
      }
    }
  });
}

/** Ключ сравнения названий как у backend (`osnova_core::prc::lookup_key`): регистр, «ё», пробелы. */
export function nameKey(value: string): string {
  return value.toLowerCase().replace(/ё/g, 'е').split(/\s+/).filter(Boolean).join(' ');
}

// ── Единицы техники ────────────────────────────────────────────────────────

export type UnitFormValues = Readonly<Record<keyof FleetUnitData, string>>;

export function unitFormValues(
  unit: FleetUnit | null,
  defaultOwner: string | null,
): UnitFormValues {
  return {
    reg_number: unit?.reg_number ?? '',
    inventory_number: unit?.inventory_number ?? '',
    vin: unit?.vin ?? '',
    owner_name: unit ? (unit.owner_name ?? '') : (defaultOwner ?? ''),
    status: unit?.status ?? 'active',
    comment: unit?.comment ?? '',
  };
}

/** Тело единицы; без номера вовсе единица не заводится. */
export function unitPayload(values: UnitFormValues): { data: FleetUnitData } | { error: string } {
  const clean = (s: string): string | null => (s.trim() === '' ? null : s.trim());
  const data: FleetUnitData = {
    reg_number: clean(values.reg_number),
    inventory_number: clean(values.inventory_number),
    vin: clean(values.vin),
    owner_name: clean(values.owner_name),
    status: isFleetStatus(values.status) ? values.status : 'active',
    comment: clean(values.comment),
  };
  if (!data.reg_number && !data.inventory_number && !data.vin) {
    return { error: 'Укажите хотя бы один номер: государственный, инвентарный или VIN' };
  }
  return { data };
}

/** Заголовок единицы в списке: госномер, иначе инвентарный, иначе VIN. */
export function unitTitle(unit: FleetUnit): string {
  return unit.reg_number ?? unit.inventory_number ?? unit.vin ?? 'Без номера';
}
